import { DonationAmount } from '@classy/campaign-page-blocks'
import { getAnalyticsInstance } from '../analytics'
import { DonationInitBlockState, DonationInitIntelligentAskState } from 'features/Block/donation'

export interface DonationFormInitPayload {
  organization_id: string
  campaign_type: string
  campaign_id: string
  cart_enabled: boolean
  dcf_enabled: boolean
  /**
   * Amount that is preselected (<Float>|null)
   */
  default_value: number | null
  currency_code: string
  default_frequency: string
  /**
   * If IAA is not enabled:
   * - a comma-separated string of the one-time amounts in the amount grid, clockwise
   *   from the top-left.
   * - the string "Custom" if Other is enabled.
   * - Example: "500,100,50,25,Custom"
   *
   * If IAA is not enabled, this field is null.
   */
  amounts_onetime: string | null
  /**
   * If IAA is not enabled:
   * - a comma-separated string of the recurring amounts in the amount grid, clockwise
   *   from the top-left.
   * - the string "Custom" if Other is enabled.
   * - Example: "50,25,10,5,Custom"
   *
   * If IAA is enabled, this field is null.
   */
  amounts_recurring: string | null
  /**
   * Is IAA enabled for one-time (<Boolean>)
   */
  iaa_enabled_onetime: boolean
  /**
   * Is IAA enabled for recurring (<Boolean>)
   */
  iaa_enabled_recurring: boolean
  /**
   * The amount we use to populate the one-time “Other” field for IAA.
   */
  iaa_amount_onetime: number
  /**
   * The amount we use to populate the recurring “Other” field for IAA.
   */
  iaa_amount_recurring: number
  /**
   * If IAA is enabled:
   * - a comma-separated string of each of the one-time amounts in the amount grid, clockwise
   *   from the top-left.
   * - includes the "other" amount.
   *
   * If IAA is not enabled, this field is null.
   */
  iaa_amounts_onetime: string | null
  /**
   * If IAA is enabled:
   * - a comma-separated string of the recurring amounts in the amount grid, clockwise
   *   from the top-left.
   * - includes the "other" amount.
   *
   * If IAA is not enabled, this field is null.
   */
  iaa_amounts_recurring: string | null
  /**
   * If the ?amount= query param was used in the URL (<Float>)
   */
  url_amount_used: number
  /**
   * The data-id of the section containing the donation form (<String>)
   */
  page_position: string
}

export interface TrackDonationFormInitParams {
  initBlockstate: DonationInitBlockState
  intelligentAskState: DonationInitIntelligentAskState
  urlAmountUsed: string
  sectionId?: string
}

interface AmountArraytoStringParams {
  amountsArray: DonationAmount[]
  iaaEnabled?: boolean
}

const amountArraytoString = ({
  amountsArray = [],
  iaaEnabled = false,
}: AmountArraytoStringParams) => {
  const amounts = amountsArray.map((amount) => {
    if (amount?.name === 'other' && !iaaEnabled) return 'Custom'
    return amount.amount
  })

  return amounts.join(',')
}

/**
 * Track DonationBlocks initialized state
 */
export const trackDonationFormInitEvent = ({
  initBlockstate,
  urlAmountUsed,
  sectionId,
  /**
   * Note, this property is set in Donation.controller.tsx, not the Donation block
   */
  intelligentAskState,
}: TrackDonationFormInitParams) => {
  const analytics = getAnalyticsInstance()

  const {
    amount,
    campaignId,
    currencyCode,
    dcfEnabled,
    frequency,
    isCartEnabled,
    orgId,
    visibleDonationAmounts,
    visibleRecurringAmounts,
  } = initBlockstate

  const { onetimeEnabled, donationOtherAmount, recurringEnabled, recurringOtherAmount } =
    intelligentAskState

  const iaaEnabled = intelligentAskState.enabled

  const heapPayload: DonationFormInitPayload = {
    organization_id: orgId || '',
    campaign_type: 'studio',
    campaign_id: campaignId || '',
    cart_enabled: isCartEnabled || false,
    dcf_enabled: dcfEnabled || false,
    default_value: amount,
    currency_code: currencyCode,
    default_frequency: frequency,
    amounts_onetime: !iaaEnabled
      ? amountArraytoString({ amountsArray: visibleDonationAmounts })
      : null,
    amounts_recurring: !iaaEnabled
      ? amountArraytoString({ amountsArray: visibleRecurringAmounts })
      : null,
    url_amount_used: Number(urlAmountUsed || 0),
    page_position: sectionId || '',
    // Intelligent Ask Amount
    iaa_enabled_onetime: onetimeEnabled,
    iaa_enabled_recurring: recurringEnabled,
    iaa_amount_onetime: Number(donationOtherAmount),
    iaa_amount_recurring: Number(recurringOtherAmount),
    iaa_amounts_onetime: iaaEnabled
      ? amountArraytoString({ amountsArray: visibleDonationAmounts, iaaEnabled })
      : null,
    iaa_amounts_recurring: iaaEnabled
      ? amountArraytoString({ amountsArray: visibleRecurringAmounts, iaaEnabled })
      : null,
  }

  // Heap
  analytics.track('studio_page/donation_form_init', heapPayload, {
    plugins: {
      all: false,
      heap: true,
    },
  })
}
